import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Baby, Stethoscope, Syringe, GraduationCap, ChevronDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const PILLARS = [
  {
    id: "expertise-card-newborn",
    icon: Baby,
    title: "Newborn & Neonatal Care",
    description: "Clinical training in neonatal care and management.",
    detail:
      "Trained in neonatal intensive care and management, sick neonatal care, and management of infectious diseases in the newborn.",
    tint: "bg-[#70435F]/10",
    tintIcon: "text-[#70435F]",
  },
  {
    id: "expertise-card-pediatrics",
    icon: Stethoscope,
    title: "General Pediatrics",
    description: "Training in pediatric outpatient and inpatient care.",
    detail:
      "Experience managing pediatric OPD and inpatient cases, common pediatric illnesses and follow-ups, and pediatric intensive care.",
    tint: "bg-[#E8C2CB]/50",
    tintIcon: "text-[#B45E74]",
  },
  {
    id: "expertise-card-immunization",
    icon: Syringe,
    title: "Immunization & Preventive Child Health",
    description: "Educational information on immunization and preventive child health.",
    detail:
      "A special interest in preventive pediatrics — helping parents understand immunization and everyday preventive child health.",
    tint: "bg-[#B89A69]/15",
    tintIcon: "text-[#9A7B4A]",
  },
  {
    id: "expertise-card-parenting",
    icon: GraduationCap,
    title: "Parenting Education",
    description: "Accessible child health information and parenting awareness.",
    detail:
      "Creator of Little Ones, an educational initiative sharing reliable content on parenting, preventive pediatrics, and child health awareness.",
    tint: "bg-[#C9A98C]/25",
    tintIcon: "text-[#8C6A50]",
  },
];

const ExpertiseCard = ({ pillar, open, onToggle, index }) => (
  <Reveal delay={0.08 * index} className="h-full">
    <article
      data-testid={pillar.id}
      className={`h-full bg-white border rounded-2xl transition-all duration-500 overflow-hidden ${
        open ? "border-[#E8C2CB] shadow-soft" : "border-[#70435F]/10 hover:-translate-y-1.5 hover:shadow-soft hover:border-[#E8C2CB]"
      }`}
    >
      <button
        data-testid={`${pillar.id}-toggle`}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${pillar.id}-panel`}
        className="w-full text-left p-7 cursor-pointer"
      >
        <span className="flex items-start justify-between gap-3 mb-6">
          <span
            className={`inline-flex w-12 h-12 rounded-full items-center justify-center transition-colors duration-500 ${
              open ? "bg-[#70435F]" : pillar.tint
            }`}
          >
            <pillar.icon
              size={22}
              strokeWidth={1.5}
              className={`transition-colors duration-500 ${open ? "text-white" : pillar.tintIcon}`}
            />
          </span>
          <span
            className={`mt-1 inline-flex w-7 h-7 rounded-full border items-center justify-center transition-all duration-500 ${
              open ? "border-[#70435F] bg-[#70435F]" : "border-[#70435F]/25"
            }`}
            aria-hidden="true"
          >
            <ChevronDown
              size={15}
              className={`transition-all duration-500 ${open ? "rotate-180 text-white" : "text-[#70435F]"}`}
            />
          </span>
        </span>
        <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#70435F] mb-3 leading-snug">
          {pillar.title}
        </h3>
        <p className="text-sm text-[#695E67] leading-relaxed">{pillar.description}</p>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${pillar.id}-panel`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-7 pb-7">
              <div className="border-t border-[#F0EAE3] pt-5">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#B89A69] mb-2">
                  Training & educational focus
                </p>
                <p className="text-sm text-[#695E67] leading-relaxed">{pillar.detail}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <span
        className={`block h-px bg-[#B89A69] mx-7 mb-6 transition-all duration-500 ${open ? "w-16" : "w-8"}`}
        aria-hidden="true"
      />
    </article>
  </Reveal>
);

const Expertise = () => {
  const [openId, setOpenId] = useState(null);

  return (
    <section id="expertise" className="py-24 sm:py-32 bg-[#FAF7F4] border-y border-[#E7DED4]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-14">
          <Eyebrow>Clinical Expertise</Eyebrow>
          <h2
            data-testid="expertise-heading"
            className="font-display text-3xl sm:text-4xl lg:text-[42px] font-medium tracking-tight text-[#383237] leading-tight mt-5"
          >
            Areas of <em className="italic text-[#70435F]">clinical focus</em>
          </h2>
          <p className="text-base text-[#695E67] leading-relaxed mt-4">
            A foundation built across neonatology, general pediatrics, immunization, and parent education.
            Select a card to read more.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {PILLARS.map((p, i) => (
            <ExpertiseCard
              key={p.id}
              pillar={p}
              index={i}
              open={openId === p.id}
              onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Expertise;
